import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { AdminHead } from "./AdminLayout";
import { ImageUpload, Field } from "./ImageUpload";
import { api, img, useAction, useGet } from "../../lib/api";

const EMPTY = { title: "", image_url: "", link_url: "", is_active: true };
const pick = ({ title, image_url, link_url, is_active }) => ({ title, image_url, link_url, is_active });

const BannerForm = ({ initial, onSave }) => {
  const [f, setF] = useState(initial);
  return (
    <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); onSave(f); }}>
      <ImageUpload value={f.image_url} onChange={(u) => setF({ ...f, image_url: u })} testid="banner-image-upload" aspect="aspect-[3/1]" />
      <Field label="Judul / teks di atas banner (opsional)"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="banner-title-input" /></Field>
      <Field label="Tautan saat banner diklik (opsional)"><Input placeholder="https://... atau /katalog" value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} data-testid="banner-link-input" /></Field>
      <label className="flex items-center gap-3 text-sm font-medium"><Switch checked={f.is_active} onCheckedChange={(v) => setF({ ...f, is_active: v })} data-testid="banner-active-switch" /> Tampilkan di beranda</label>
      <button type="submit" disabled={!f.image_url} className="btn-forest w-full disabled:opacity-50" data-testid="banner-save-button">Simpan Banner</button>
    </form>
  );
};

export default function AdminBanners() {
  const { data } = useGet("banners", "/banners");
  const act = useAction(["banners", "banners-active", "stats"]);
  const [editing, setEditing] = useState(null);

  const save = async (f) => {
    const ok = await act(() => (editing.id ? api.put(`/banners/${editing.id}`, f) : api.post("/banners", f)), "Banner disimpan");
    if (ok) setEditing(null);
  };
  const toggle = (b, v) => act(() => api.put(`/banners/${b.id}`, { ...pick(b), is_active: v }), v ? "Banner ditampilkan" : "Banner disembunyikan");
  const remove = (b) => window.confirm("Hapus banner ini?") && act(() => api.delete(`/banners/${b.id}`), "Banner dihapus");

  return (
    <>
      <AdminHead title="Banner Event" desc="Banner aktif terbaru tampil di bawah header halaman beranda.">
        <button className="btn-gold" onClick={() => setEditing(EMPTY)} data-testid="banner-add-button"><Plus className="h-4 w-4" /> Tambah Banner</button>
      </AdminHead>
      <div className="grid gap-5 md:grid-cols-2">
        {(data || []).map((b) => (
          <div key={b.id} className="overflow-hidden rounded-3xl border bg-white" data-testid={`banner-item-${b.id}`}>
            <img src={img(b.image_url)} alt="" className="aspect-[3/1] w-full object-cover" />
            <div className="flex items-center justify-between gap-3 p-5">
              <div className="min-w-0">
                <p className="truncate font-medium">{b.title || "(Tanpa judul)"}</p>
                <p className="truncate text-xs text-[var(--ink)]/50">{b.link_url || "Tanpa tautan"}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch checked={b.is_active} onCheckedChange={(v) => toggle(b, v)} data-testid={`banner-toggle-${b.id}`} />
                <button onClick={() => setEditing(b)} className="rounded-full p-2 hover:bg-[var(--cream)]" data-testid={`banner-edit-${b.id}`}><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(b)} className="rounded-full p-2 text-red-600 hover:bg-red-50" data-testid={`banner-delete-${b.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {data?.length === 0 && <p className="text-[var(--ink)]/60">Belum ada banner.</p>}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="banner-dialog">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit Banner" : "Tambah Banner"}</DialogTitle></DialogHeader>
          {editing && <BannerForm initial={pick(editing)} onSave={save} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
